import React, { useState } from 'react';

const MicroservicesDetail = () => {
  const [expandedService, setExpandedService] = useState(null);

  const services = [
    {
      id: 'backend',
      name: 'Backend API Server',
      binary: '/opt/GoSMPP/bin/backend',
      source: 'cmd/server/main.go',
      systemd: 'gosmpp-backend.service',
      port: 8092,
      status: 'Production',
      color: '#3B82F6',
      description: 'The main REST API server that handles all HTTP requests from the frontend and external APIs. Manages authentication, user management, DLT operations, SMPP connector management, reporting, and admin functions.',
      responsibilities: [
        'User authentication (JWT + Redis sessions)',
        'REST API for frontend (admin panel, user dashboard)',
        'SMS submission via GUI and HTTP API',
        'SMPP connector management (start/stop/restart via API)',
        'DLT entity, header, and template CRUD',
        'Plan and subscription management',
        'Reporting and analytics queries',
        'Balance and recharge operations',
        'Telegram alert management',
        'Worker management API',
        'Knowledge Base CRUD',
        'SMPP inbound server (port 2776)',
      ],
      databases: [
        { name: 'way2connect', purpose: 'Primary — users, DLT, plans, routing, config' },
        { name: 'way2connect_reporting', purpose: 'SMS reports, DLR, billing logs' },
        { name: 'way2connect_worker', purpose: 'Worker registry, ingest queue, submission queue' },
        { name: 'way2connect_logs', purpose: 'SMPP logs, connection history, error logs' },
        { name: 'way2connect_alert', purpose: 'Alert rules, Telegram bots, alert history' },
        { name: 'way2connect_kb', purpose: 'Knowledge base articles, categories' },
        { name: 'way2connect_migration', purpose: 'Data migration jobs and logs' },
        { name: 'way2connect_support', purpose: 'Support tickets, chat, agents' },
      ],
      config: {
        file: '/opt/GoSMPP/config/config.yaml',
        key_settings: [
          'server.host: 0.0.0.0',
          'server.port: 8092',
          'server.mode: release',
          'jwt.secret: [64-char random]',
          'jwt.expiry_hours: 0.5 (30 min)',
          'redis.host: localhost:6379',
          'smpp.inbound_port: 2776',
        ]
      },
      dependencies: ['MySQL 8.0', 'Redis', 'Nginx (reverse proxy)'],
      capacity: {
        concurrent_connections: '1000+',
        api_throughput: '~5000 req/sec',
        memory: '~30-50 MB',
        cpu: 'Low (event-driven)',
      },
      flow: [
        'Nginx receives HTTP request on port 80/443',
        'Nginx proxies /api/v1/* to localhost:8092',
        'Gin router matches endpoint',
        'Middleware: CORS → Auth (JWT + Redis) → Role check',
        'Handler processes request (DB queries, business logic)',
        'Response returned as JSON',
      ],
      endpoints_count: '50+',
      key_middleware: ['LoginRateLimitMiddleware', 'AuthMiddleware', 'RoleMiddleware', 'APIKeyMiddleware'],
    },
    {
      id: 'http-worker',
      name: 'HTTP Submit Worker',
      binary: '/var/www/html/GoSMPP-Server/backend/bin/http-submit-worker',
      source: 'cmd/http-submit-worker/main.go',
      systemd: 'http-submit-worker.service',
      port: null,
      status: 'Production',
      color: '#10B981',
      description: 'Background worker that picks up SMS from the ingest queue and delivers them via HTTP API to operators. Handles only delivery_type=HTTP messages.',
      responsibilities: [
        'Poll sms_ingest_requests for HTTP delivery messages',
        'Resolve routing: Entity HTTP Routing → Plan HTTP Mapping',
        'Build HTTP API request with operator credentials',
        'Submit SMS to operator HTTP endpoint',
        'Record result in sms_reports (way2connect_reporting)',
        'Handle retries on failure',
        'Deduct balance on successful submission',
      ],
      databases: [
        { name: 'way2connect', purpose: 'Read user plans, routing config, entity mappings' },
        { name: 'way2connect_reporting', purpose: 'Write SMS reports, billing logs' },
      ],
      config: {
        file: '/var/www/html/GoSMPP-Server/backend/config/config.yaml',
        key_settings: [
          'Uses same config as backend',
          'WorkerMode: HTTP',
          'SQL filter: delivery_type = "HTTP" or NULL',
          'Polling interval: 1 second',
          'Batch size: configurable',
        ]
      },
      dependencies: ['MySQL 8.0'],
      capacity: {
        throughput: '~1000 SMS/sec',
        memory: '~20-30 MB',
        cpu: 'Medium (HTTP calls)',
        concurrent_submissions: '10 goroutines',
      },
      flow: [
        'Poll way2connect_worker.sms_ingest_requests WHERE delivery_type=HTTP',
        'Pick up batch of unprocessed messages',
        'For each message: resolve routing chain',
        'Routing: Entity HTTP Routing → Plan HTTP Route → BLOCK',
        'Build HTTP POST/GET to operator API endpoint',
        'Send request with operator credentials',
        'Parse response, update sms_reports with status',
        'Deduct balance from user account',
        'Mark ingest request as processed',
      ],
      key_middleware: [],
    },
    {
      id: 'smpp-worker',
      name: 'SMPP Worker',
      binary: '/var/www/html/GoSMPP-Server/backend/bin/smpp-worker',
      source: 'cmd/smpp-worker/main.go',
      systemd: 'smpp-worker.service',
      port: null,
      status: 'Production',
      color: '#8B5CF6',
      description: 'Background worker that owns all outbound SMPP connections. Picks up SMS from the ingest queue with delivery_type=SMPP and submits them via SMPP protocol to operators.',
      responsibilities: [
        'Own and manage all outbound SMPP connections',
        'Poll sms_ingest_requests for SMPP delivery messages',
        'Resolve routing: Plan SMPP Mapping → Connector',
        'Build SMPP submit_sm PDU with DLT TLVs',
        'Send via SMPP connection to operator',
        'Track sequence numbers for async response correlation',
        'Handle submit_sm_resp and update sms_reports',
        'Process enquire_link keepalive',
        'Handle multi-part (long) SMS with UDH',
        'Auto-detect Unicode for Hindi/non-Latin content',
      ],
      databases: [
        { name: 'way2connect', purpose: 'Read SMPP connectors, plans, routing' },
        { name: 'way2connect_reporting', purpose: 'Write SMS reports, operator responses' },
      ],
      config: {
        file: '/var/www/html/GoSMPP-Server/backend/config/config.yaml',
        key_settings: [
          'WorkerMode: SMPP',
          'SQL filter: delivery_type = "SMPP"',
          'SMPP bind_type: TRX (Transceiver)',
          'Enquire link interval: 30s',
          'Read/Write timeout: 30s',
        ]
      },
      dependencies: ['MySQL 8.0', 'SMPP Operator (e.g. way2trmpg)'],
      capacity: {
        throughput: '~500-2000 SMS/sec per connector',
        memory: '~30-50 MB',
        cpu: 'Medium (PDU encoding)',
        connections: '1 per operator connector',
      },
      flow: [
        'Start SMPP ConnectionManager on boot',
        'Bind to operator SMPP server (TRX mode)',
        'Poll way2connect_worker.sms_ingest_requests WHERE delivery_type=SMPP',
        'For each message: resolve Plan SMPP Mapping',
        'Build submit_sm PDU (source, dest, message, DLT TLVs)',
        'Handle Unicode auto-detection (GSM7 vs UCS2)',
        'Handle long SMS: split into segments with UDH',
        'Send submit_sm via SMPP connection',
        'Track sequence_number → message mapping',
        'Receive submit_sm_resp asynchronously',
        'Update sms_reports with operator message ID and status',
        'Deduct balance on successful submission',
      ],
      key_middleware: [],
    },
    {
      id: 'sms-ingest',
      name: 'SMS Ingest Service',
      binary: 'Embedded in Backend',
      source: 'internal/services/sms_ingest_service.go',
      systemd: 'Part of gosmpp-backend',
      port: null,
      status: 'Production',
      color: '#F59E0B',
      description: 'Service within the backend that accepts SMS from all channels (GUI, HTTP API, SMPP inbound) and writes them to the ingest queue for processing by workers.',
      responsibilities: [
        'Accept SMS from GUI (Send SMS page)',
        'Accept SMS from HTTP API (/api/v1/sendsms, /api/v1/sms/send)',
        'Accept SMS from SMPP inbound connections',
        'Validate DLT compliance (entity, header, template)',
        'Write to sms_ingest_requests queue',
        'Trigger async routing for plan-based delivery',
        'Batch insert support for bulk SMS',
      ],
      databases: [
        { name: 'way2connect', purpose: 'Read DLT entities, headers, templates for validation' },
        { name: 'way2connect_worker', purpose: 'Write to sms_ingest_requests queue' },
      ],
      config: {
        file: 'N/A (uses backend config)',
        key_settings: [
          'Batch size: 500 per insert',
          'Supports GUI, HTTP POST, HTTP GET channels',
          'DLT validation before ingestion',
        ]
      },
      dependencies: ['MySQL 8.0'],
      capacity: {
        throughput: '~10,000 SMS/sec (batch insert)',
        memory: 'Shared with backend',
        cpu: 'Low (INSERT only)',
      },
      flow: [
        'User sends SMS via GUI / HTTP API / SMPP',
        'Backend handler validates request (auth, DLT, balance)',
        'SMSIngestService.IngestSMS() called',
        'Payload written to way2connect_worker.sms_ingest_requests',
        'Async: SMSRoutingService.RouteSMS() determines delivery_type',
        'Workers (HTTP/SMPP) pick up from queue based on delivery_type',
      ],
      key_middleware: [],
    },
  ];

  const infrastructureComponents = [
    {
      name: 'Nginx',
      type: 'Reverse Proxy',
      port: '80, 443',
      config: '/etc/nginx/conf.d/gosmpp.conf',
      purpose: 'SSL termination (via Cloudflare), request routing, rate limiting, security headers, static file serving',
      details: [
        'Routes /api/v1/* → Backend (port 8092)',
        'Routes /api/* → API Gateway (port 8080)',
        'Serves React build from /opt/GoSMPP/frontend/build',
        'Rate limiting: 10 req/min on /api/v1/login',
        'Security headers: X-Frame-Options, HSTS, XSS Protection',
        'Cloudflare handles SSL termination (no HTTP→HTTPS redirect)',
      ]
    },
    {
      name: 'Redis',
      type: 'In-Memory Store',
      port: '6379',
      config: '/etc/redis/redis.conf',
      purpose: 'Session management, token blacklisting, dashboard caching, routing cache',
      details: [
        'JWT session storage with 30-min sliding TTL',
        'Token blacklist for logout',
        'Dashboard counter caching',
        'User plan routing cache (5-min TTL)',
        'Key patterns: session:{user_id}:{token_id}, token_mapping:{jwt}, token_blacklist:{jwt}',
      ]
    },
    {
      name: 'MySQL 8.0',
      type: 'Database',
      port: '3306',
      config: '/etc/my.cnf',
      purpose: 'Primary data store for all application data across 8 databases',
      details: [
        'way2connect: Primary (users, DLT, plans, routing)',
        'way2connect_reporting: SMS reports, DLR, billing',
        'way2connect_worker: Ingest queue, submission queue, workers',
        'way2connect_logs: SMPP logs, connection history',
        'way2connect_alert: Telegram alerts, rules',
        'way2connect_kb: Knowledge base articles',
        'way2connect_migration: Data migration',
        'way2connect_support: Support tickets, chat',
      ]
    },
    {
      name: 'Cloudflare',
      type: 'CDN / DNS / SSL',
      port: 'N/A',
      config: 'Cloudflare Dashboard',
      purpose: 'DNS management, SSL certificate, DDoS protection, CDN caching',
      details: [
        'Domain: mysmpp.in',
        'SSL Mode: Full (origin has self-signed cert)',
        'Handles SSL termination before forwarding to origin',
        'DDoS protection and WAF',
      ]
    },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .shadow-lg, .shadow-md { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Microservices & Infrastructure</h1>
          <p className="mt-1 text-gray-600">Detailed documentation of every service, worker, and infrastructure component</p>
        </div>
        <div className="no-print flex gap-2">
          <button onClick={() => window.history.back()} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">← Back</button>
          <button onClick={() => window.print()} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">🖨️ Print</button>
        </div>
      </div>

      {/* Service Cards */}
      {services.map((svc, idx) => (
        <div key={svc.id} className={`mb-6 bg-white rounded-xl border border-gray-200 overflow-hidden ${idx > 0 ? 'print-break' : ''}`}>
          {/* Service Header */}
          <div
            className="p-6 cursor-pointer hover:bg-gray-50 transition-colors no-print"
            onClick={() => setExpandedService(expandedService === svc.id ? null : svc.id)}
            style={{ borderLeft: `4px solid ${svc.color}` }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: svc.color }}>
                  {svc.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{svc.name}</h2>
                  <p className="text-sm text-gray-500">{svc.systemd} • Port {svc.port || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">{svc.status}</span>
                <span className="text-gray-400 text-xl">{expandedService === svc.id ? '▼' : '▶'}</span>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-600">{svc.description}</p>
          </div>

          {/* Print version always shows, screen version is toggled */}
          <div className={`${expandedService === svc.id ? 'block' : 'hidden'} print:!block`} style={{ borderLeft: `4px solid ${svc.color}` }}>
            <div className="px-6 pb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Responsibilities */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Responsibilities</h3>
                <ul className="space-y-1.5">
                  {svc.responsibilities.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Processing Flow */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Processing Flow</h3>
                <ol className="space-y-2">
                  {svc.flow.map((step, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: svc.color }}>
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>

              {/* Databases Used */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Databases Used</h3>
                <div className="space-y-2">
                  {svc.databases.map((db, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className="font-mono text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded flex-shrink-0">{db.name}</span>
                      <span className="text-gray-500">{db.purpose}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Configuration */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Configuration</h3>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-2">File: <code className="text-gray-700">{svc.config.file}</code></div>
                  <ul className="space-y-1">
                    {svc.config.key_settings.map((s, i) => (
                      <li key={i} className="text-xs font-mono text-gray-600">{s}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Capacity */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Capacity & Resources</h3>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(svc.capacity).map(([key, val]) => (
                    <div key={key} className="bg-blue-50 rounded-lg p-2.5">
                      <div className="text-xs text-blue-600 capitalize">{key.replace(/_/g, ' ')}</div>
                      <div className="text-sm font-semibold text-blue-900">{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dependencies */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Dependencies</h3>
                <div className="flex flex-wrap gap-2">
                  {svc.dependencies.map((dep, i) => (
                    <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{dep}</span>
                  ))}
                </div>
                {svc.key_middleware && svc.key_middleware.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-1">Key Middleware:</div>
                    <div className="flex flex-wrap gap-1">
                      {svc.key_middleware.map((mw, i) => (
                        <code key={i} className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded">{mw}</code>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Binary & Source */}
              <div className="lg:col-span-2">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Deployment Info</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Binary Path</div>
                    <code className="text-xs text-gray-800 break-all">{svc.binary}</code>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Source Code</div>
                    <code className="text-xs text-gray-800">{svc.source}</code>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Systemd Service</div>
                    <code className="text-xs text-gray-800">{svc.systemd}</code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Infrastructure Components */}
      <div className="print-break mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Infrastructure Components</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {infrastructureComponents.map((comp, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-sm">
                  {comp.type === 'Reverse Proxy' ? '🔀' : comp.type === 'In-Memory Store' ? '⚡' : comp.type === 'Database' ? '🗄️' : '☁️'}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{comp.name}</h3>
                  <p className="text-xs text-gray-500">{comp.type} • Port {comp.port}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">{comp.purpose}</p>
              <ul className="space-y-1">
                {comp.details.map((d, j) => (
                  <li key={j} className="text-xs text-gray-500 flex items-start gap-1.5">
                    <span className="text-gray-400 mt-0.5">•</span>
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MicroservicesDetail;
